/**
 * Macros: a reusable piece of drawing (a selection of elements and wires), saved in the user's
 * library and inserted into any project like a paste. User symbols used by the macro travel
 * with it, so it works in projects that do not have them yet.
 */
import type { CustomSymbol, Id, Op, Project } from '@elec/domain';
import { copySelection, type ClipboardData } from './operations.ts';

export interface Macro {
  id: string;
  name: string;
  /** Sheet kind it was made on (offered first there). */
  sheetKind: string;
  data: ClipboardData;
  symbols: CustomSymbol[];
  createdAt: string;
}

export interface MacroLibraryFile {
  app: 'tablo' | 'elec';
  kind: 'macros';
  macros: Macro[];
}

export function makeMacro(project: Project, ids: Iterable<Id>, name: string, id: string): Macro {
  const data = copySelection(project, ids);
  // A macro is a drawing, not part of this project's circuits.
  const elements = data.elements.map(({ circuitId: _c, ...e }) => e);
  const used = new Set(elements.map((e) => e.symbolId));
  const sheetKind = elements[0] ? (project.sheets[elements[0].sheetId]?.kind ?? '') : '';
  return {
    id,
    name,
    sheetKind,
    data: { ...data, elements },
    symbols: Object.values(project.symbols ?? {}).filter((s) => used.has(s.id)),
    createdAt: new Date().toISOString(),
  };
}

/** Ops adding the user symbols a macro needs and the project lacks. */
export function macroSymbolOps(project: Project, macro: Macro): Op[] {
  return macro.symbols
    .filter((s) => !project.symbols?.[s.id])
    .map((s) => ({ op: 'add', coll: 'symbols', value: s }) as Op);
}

export function isMacroLibraryFile(v: unknown): v is MacroLibraryFile {
  return (
    !!v &&
    typeof v === 'object' &&
    ['tablo', 'elec'].includes((v as MacroLibraryFile).app) &&
    (v as MacroLibraryFile).kind === 'macros' &&
    Array.isArray((v as MacroLibraryFile).macros)
  );
}
